import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post, qs } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { dateTime } from '@/lib/utils';
import { fallbackInterval, useRealtimeInvalidate } from '@/lib/realtime';
import { DataTable, Dialog, PageHeader } from '@/components/ui/layout';
import { Button, Field, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';

interface PendingCosign {
  id: string;
  closedAt: string | null;
  chiefComplaint: string | null;
  patient: { id: string; mrn: string; fullName: string };
  doctor: { id: string; name: string };
  diagnoses: { icdCode: string; icdTitle: string }[];
}

export function CosignPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [signing, setSigning] = useState<PendingCosign | null>(null);
  const [note, setNote] = useState('');

  const live = useRealtimeInvalidate(['cosign'], null, [['cosign-queue']]);
  const pending = useQuery({
    queryKey: ['cosign-queue', cursor],
    queryFn: () => get<{ data: PendingCosign[]; nextCursor: string | null }>(`/encounters/cosign-queue${qs({ cursor })}`),
    refetchInterval: fallbackInterval(live, 60_000),
  });

  const cosign = useMutation({
    mutationFn: (row: PendingCosign) => post(`/encounters/${row.id}/cosign`, { note: note.trim() || undefined }),
    onSuccess: () => {
      toast.success(tr('opd', 'co_signed'));
      setSigning(null);
      setNote('');
      void queryClient.invalidateQueries({ queryKey: ['cosign-queue'] });
    },
    onError: (error) => toast.error(tr('opd', 'could_not_co_sign'), describeError(error)),
  });

  return (
    <>
      <PageHeader title={tr('opd', 'co_sign')} description={tr('opd', 'consultations_closed_by_medical_officers')} />
      <DataTable
        rows={pending.data?.data}
        loading={pending.isLoading} refreshing={pending.isFetching}
        error={pending.error}
        rowKey={(row) => row.id}
        empty={tr('opd', 'nothing_is_waiting_for_a_co_signature')}
        columns={[
          { header: tr('opd', 'closed'), sort: (row) => row.closedAt, cell: (row) => (row.closedAt ? dateTime(row.closedAt) : '—') },
          {
            header: tr('opd', 'patient'),
            sort: (row) => row.patient.fullName,
            cell: (row) => (
              <div>
                <p className="font-medium">{row.patient.fullName}</p>
                <p className="text-xs text-muted-foreground">{row.patient.mrn}</p>
              </div>
            ),
          },
          { header: tr('opd', 'seen_by'), sort: (row) => row.doctor.name, cell: (row) => row.doctor.name },
          {
            header: tr('opd', 'diagnosis'),
            cell: (row) => (row.diagnoses[0] ? `${row.diagnoses[0].icdCode} — ${row.diagnoses[0].icdTitle}` : (row.chiefComplaint ?? '—')),
          },
          {
            header: '',
            align: 'right',
            cell: (row) => (
              <div className="flex justify-end gap-1">
                <Button size="sm" variant="outline" onClick={() => navigate(`/consultations/${row.id}`)}>
                  {tr('opd', 'read')}
                </Button>
                <Button size="sm" onClick={() => setSigning(row)}>
                  {tr('opd', 'co_sign')}
                </Button>
              </div>
            ),
          },
        ]}
        footer={
          pending.data?.nextCursor ? (
            <Button variant="outline" className="ms-auto" onClick={() => setCursor(pending.data?.nextCursor ?? undefined)}>
              {tr('opd', 'next_page')}
            </Button>
          ) : undefined
        }
      />
      {signing && (
        <Dialog
          open
          onClose={() => setSigning(null)}
          title={tr('opd', 'co_sign_x', { fullName: signing.patient.fullName })}
          description={tr('opd', 'seen_by_x', { name: signing.doctor.name })}
          footer={
            <>
              <Button variant="ghost" onClick={() => setSigning(null)}>
                {tr('opd', 'cancel')}
              </Button>
              <Button onClick={() => cosign.mutate(signing)} loading={cosign.isPending}>
                {tr('opd', 'co_sign')}
              </Button>
            </>
          }
        >
          <Field label={tr('opd', 'note_optional')}>
            <Textarea rows={3} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
        </Dialog>
      )}
    </>
  );
}
